import React from 'react';

/**
 * Reusable UI Components
 * Common components used across the application
 */

/**
 * Status Badge Component
 */
export const StatusBadge = ({ status, size = 'md', showDot = true }) => {
  const statusConfig = {
    success: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)', text: 'Success' },
    error: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', text: 'Error' },
    warning: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', text: 'Warning' },
    info: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', text: 'Info' },
    pending: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', text: 'Pending' },
    completed: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)', text: 'Completed' },
    overdue: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', text: 'Overdue' },
    active: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)', text: 'Active' },
    inactive: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)', text: 'Inactive' },
    draft: { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', text: 'Draft' },
  };

  const config = statusConfig[status?.toLowerCase()] || statusConfig.info;
  
  const sizeClasses = {
    sm: 'status-badge-sm',
    md: 'status-badge-md',
    lg: 'status-badge-lg',
  };

  return (
    <span 
      className={`status-badge ${sizeClasses[size]}`}
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
      }}
      data-testid={`status-badge-${status}`}
    >
      {showDot && <span className="status-dot" style={{ backgroundColor: config.color }} />}
      <span className="status-text">{config.text}</span>
    </span>
  );
};

/**
 * Loading Spinner Component
 */
export const LoadingSpinner = ({ size = 'md', fullScreen = false }) => {
  const sizeClasses = {
    sm: 'spinner-sm',
    md: 'spinner-md',
    lg: 'spinner-lg',
    xl: 'spinner-xl',
  };

  const spinner = (
    <div className={`loading-spinner-container ${sizeClasses[size]}`}>
      <div className="loading-spinner" role="status" aria-label="Loading">
        <div className="spinner-circle" />
      </div>
      <span className="loading-text">Loading...</span>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="loading-full-screen" data-testid="loading-spinner">
        {spinner}
      </div>
    );
  }

  return spinner;
};

/**
 * Button Component
 */
export const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  disabled = false,
  loading = false,
  icon: Icon,
  className = '',
  ...props 
}) => {
  const variantClasses = {
    primary: 'mf-btn-primary',
    secondary: 'mf-btn-secondary',
    outline: 'mf-btn-outline',
    danger: 'mf-btn-danger',
    ghost: 'mf-btn-ghost',
  };

  const sizeClasses = {
    sm: 'mf-btn-sm',
    md: 'mf-btn-md',
    lg: 'mf-btn-lg',
  };

  return (
    <button
      className={`mf-btn ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      disabled={disabled || loading}
      data-testid={props['data-testid'] || 'button'}
      {...props}
    >
      {loading ? (
        <span className="btn-loading">
          <LoadingSpinner size="sm" />
        </span>
      ) : Icon ? (
        <Icon size={16} style={{ marginRight: '8px' }} />
      ) : null}
      {children}
    </button>
  );
};

/**
 * Card Component
 */
export const Card = ({ children, className = '', title, subtitle, actions }) => (
  <div className={`mf-card ${className}`} data-testid="card">
    {(title || subtitle || actions) && (
      <div className="mf-card-header">
        <div className="mf-card-title">
          {title && <h3 className="mf-card-title-text">{title}</h3>}
          {subtitle && <p className="mf-card-subtitle">{subtitle}</p>}
        </div>
        {actions && <div className="mf-card-actions">{actions}</div>}
      </div>
    )}
    <div className="mf-card-body">
      {children}
    </div>
  </div>
);

/**
 * Alert Component
 */
export const Alert = ({ type = 'info', children, title }) => {
  const typeConfig = {
    info: { icon: 'ℹ️', bg: '#eff6ff', border: '#3b82f6' },
    success: { icon: '✓', bg: '#f0fdf4', border: '#22c55e' },
    warning: { icon: '⚠️', bg: '#fffbeb', border: '#f59e0b' },
    error: { icon: '✕', bg: '#fef2f2', border: '#ef4444' },
  };

  const config = typeConfig[type];

  return (
    <div 
      className="mf-alert"
      style={{ backgroundColor: config.bg, borderLeft: `4px solid ${config.border}` }}
      role="alert"
      data-testid={`alert-${type}`}
    >
      {title && <div className="mf-alert-title">{title}</div>}
      <div className="mf-alert-content">{children}</div>
    </div>
  );
};

/**
 * Tooltip Component
 */
export const Tooltip = ({ content, children, position = 'top' }) => {
  const [isVisible, setIsVisible] = React.useState(false);

  const positionClasses = {
    top: 'tooltip-top',
    bottom: 'tooltip-bottom',
    left: 'tooltip-left',
    right: 'tooltip-right',
  };

  return (
    <div 
      className="tooltip-container"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      data-testid="tooltip"
    >
      {children}
      {isVisible && (
        <div className={`tooltip ${positionClasses[position]}`}>
          {content}
        </div>
      )}
    </div>
  );
};

/**
 * Modal Component — with focus trap, Escape-to-close, and ARIA attributes.
 */
export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  const modalRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Tab') {
        const focusable = modalRef.current?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.activeElement;
    modalRef.current?.querySelector('button, [href], input, select, textarea')?.focus();
    return () => { document.removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'modal-sm',
    md: 'modal-md',
    lg: 'modal-lg',
    xl: 'modal-xl',
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="modal" role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-heading' : undefined}>
      <div 
        ref={modalRef}
        className={`modal-content ${sizeClasses[size]}`}
        onClick={e => e.stopPropagation()}
      >
        {title && (
          <div className="modal-header">
            <h2 id="modal-heading" className="modal-title">{title}</h2>
            <button className="modal-close" onClick={onClose} aria-label="Close dialog">&times;</button>
          </div>
        )}
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

/**
 * Empty State Component
 */
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="empty-state" data-testid="empty-state">
    {Icon && <Icon size={48} className="empty-state-icon" />}
    <h3 className="empty-state-title">{title}</h3>
    {description && <p className="empty-state-description">{description}</p>}
    {action && <div className="empty-state-action">{action}</div>}
  </div>
);

export default {
  StatusBadge,
  LoadingSpinner,
  Button,
  Card,
  Alert,
  Tooltip,
  Modal,
  EmptyState,
};
